import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = ["Wedding", "Corporate Event", "Private Party", "Birthday", "Launch / Brand", "Other"];

const empty = { name: "", email: "", phone: "", event_date: "", event_type: "", guests: "", details: "" };

const inputCls =
  "w-full bg-[#050505] border border-[#222] px-4 py-3.5 text-white text-sm placeholder:text-[#5a5a5a] focus:outline-none focus:border-gold transition-colors duration-300";

export default function Booking() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_date || !form.event_type) {
      toast.error("Please fill in your name, email, date and event type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Request sent — we'll be in touch within 24 hours.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="py-28 md:py-40 px-6 md:px-12 bg-[#0a0a0a]" data-testid="booking-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Reserve</p>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95] mb-8">
            Let's build
            <br />
            <span className="italic gold-shimmer">your bar.</span>
          </h2>
          <p className="text-[#a0a0a0] font-light leading-relaxed max-w-md mb-12">
            Tell us about your event and we'll craft a proposal — menu, setup, and quote —
            tailored to your celebration.
          </p>

          <div className="flex flex-col gap-5">
            <a href="tel:+16464009752" className="flex items-center gap-4 group" data-testid="booking-phone">
              <span className="w-11 h-11 grid place-items-center border border-[#222] group-hover:border-gold transition-colors">
                <Phone size={16} className="text-gold" />
              </span>
              <span className="text-white/90 group-hover:text-gold transition-colors">+ 1 (646) 400-9752</span>
            </a>
            <a href="mailto:support@bartendingizlife.com" className="flex items-center gap-4 group" data-testid="booking-email">
              <span className="w-11 h-11 grid place-items-center border border-[#222] group-hover:border-gold transition-colors">
                <Mail size={16} className="text-gold" />
              </span>
              <span className="text-white/90 group-hover:text-gold transition-colors">support@bartendingizlife.com</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 grid place-items-center border border-[#222]">
                <MapPin size={16} className="text-gold" />
              </span>
              <span className="text-white/90">New York City & Tri-State Area</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="booking-form">
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Name *</label>
              <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="booking-input-name" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Email *</label>
              <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@email.com" data-testid="booking-input-email" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Phone</label>
              <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(646) 000-0000" data-testid="booking-input-phone" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Event Date *</label>
              <input type="date" className={`${inputCls} [color-scheme:dark]`} value={form.event_date} onChange={set("event_date")} data-testid="booking-input-date" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Event Type *</label>
              <select className={`${inputCls} appearance-none cursor-pointer`} value={form.event_type} onChange={set("event_type")} data-testid="booking-select-type">
                <option value="" disabled>Select type</option>
                {eventTypes.map((t) => (
                  <option key={t} value={t} className="bg-[#0a0a0a]">{t}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Guests</label>
              <input className={inputCls} value={form.guests} onChange={set("guests")} placeholder="Approx. count" data-testid="booking-input-guests" />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Details</label>
              <textarea rows={4} className={`${inputCls} resize-none`} value={form.details} onChange={set("details")} placeholder="Tell us about your event, venue, and vibe..." data-testid="booking-input-details" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit"
              className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-3 bg-gold text-black py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#e5c158] active:scale-[0.99] transition-all duration-300 disabled:opacity-60"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Sending</> : "Request a Quote"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
